import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { decode, decodeImage, encodeImage, toRGBA8 } from 'utif';
@Injectable({
	providedIn: 'root'
})
export class FileService {
	fileTypes = [
		// 'image/png',
		'image/jpeg',
		'application/pdf',
		'image/tiff'
	];

	/**
	 * validate file extension
	 * @param file - current file
	 */
	validFileType = (file: File) => this.fileTypes.includes(file.type);

	/**
	 * return file size
	 * @param number - raw file size
	 */
	returnFileSize(number: number, decimals = 0) {
		let unit = 'B';
		if (number > 1000) {
			number /= 1000;
			unit = 'KB';
			if (number > 1000) {
				number /= 1000;
				unit = 'MB';
			}
		}
		/*
		if (number > 1024) {
			number /= 1024;
			unit = 'KiB';
			if (number > 1024) {
				number /= 1024;
				unit = 'MiB';
			}
		}
		*/
		return `${number.toFixed(decimals)} ${unit}`;
	}

	/**
    * returns a File with a smaller size of the provided image file
    * @param targetSize - the desired maximum size for the image
    * @param file - the file to be shrinked
    * @param compressionRatio - (default: 0.9) when the file has a lossy compression, the desired compression ratio
    */
	getSmallerImage = (targetSize: number, file: File, compressionRatio = 0.9): Observable<File> => {
		return Observable.create(obs => {
			const reader = new FileReader(),
				isTIFF = file.type === 'image/tiff',
				canvas = document.createElement('canvas'),
				ctx = canvas.getContext('2d'),
				sizeIsOK = (current: number, target: number) => current < target && current > 0.8 * target;
			reader.onload = () => {
				const getNewSize = (): { width: number; height: number } => {
					const ratio = Math.max(Math.floor(Math.sqrt(targetSize / newSize) * 1000) / 1000, 0.001);
					// if file weight was linear with dimension in pixel, this would be the perfect size

					return {
						width: Math.floor(width * ratio),
						height: Math.floor(height * ratio)
					};
				};
				let newSize = file.size,
					width: number,
					height: number,
					shrinkImage: Function;
				if (isTIFF) {
					const ifd = decode(reader.result as ArrayBuffer),
						imageCanvas = document.createElement('canvas');
					decodeImage(reader.result as ArrayBuffer, ifd[0]);
					; ({ width, height } = ifd[0]); // the semicolon is for avoiding potential previous lines with no semicolon to be interpreted as function call
					imageCanvas.width = width;
					imageCanvas.height = height;

					imageCanvas.getContext('2d').putImageData(
						toRGBA8(ifd[0]).reduce(
							(acc, byte, i) => { acc.data[i] = byte; return acc; },
							imageCanvas.getContext('2d').createImageData(width, height)),
						0,
						0);

					shrinkImage = () => {
						({ width, height } = getNewSize());
						canvas.width = width;
						canvas.height = height;
						ctx.drawImage(imageCanvas, 0, 0, width, height);

						const ei = encodeImage(
							new Uint8Array(ctx.getImageData(0, 0, width, height).data.buffer),
							width,
							height
						);

						if (sizeIsOK(newSize = ei.byteLength, targetSize)) {
							obs.next(new File([ei], file.name, {
								type: 'image/tiff'
							}));
							/*
								const link = document.createElement('a');
								link.href = URL.createObjectURL(
									new File([ei], file.name, {
										type: 'image/tiff'
									}));
								link.download = "prova.tiff";

								link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
								URL.revokeObjectURL(link.href);
							*/
						}
						else {
							ctx.clearRect(0, 0, width, height);
							shrinkImage();
						}
						shrinkImage();
					};
				}
				else {
					const img = new Image();
					img.onload = () => {
						width = img.naturalWidth;
						height = img.naturalHeight;

						shrinkImage = () => {
							({ width, height } = getNewSize());
							canvas.width = width;
							canvas.height = height;

							ctx.drawImage(img, 0, 0, width, height);

							canvas.toBlob(blob => { // WARNING: this is asynchronous
								if (sizeIsOK(newSize = blob.size, targetSize)) {
									obs.next(new File([blob], file.name, {
										type: file.type,
									}));
								}
								else {
									ctx.clearRect(0, 0, width, height);
									shrinkImage();
								}
							}, file.type, compressionRatio);
						};
						shrinkImage();
					};

					img.src = reader.result as string;
				}
			};
			if (isTIFF) {
				reader.readAsArrayBuffer(file);
			}
			else {
				reader.readAsDataURL(file);
			}
		});
	}

}
